import React from 'react'
import {useQuery} from '@apollo/client'
import {Link, RouteComponentProps} from '@reach/router'
import {
  format as formatDate,
  parseISO as parseDateISO,
} from 'date-fns'
import {CalendarEvent, BagCheckFill, BagXFill} from 'react-bootstrap-icons'

import {fromGlobalId} from '../../utils/graphql'
import {ensureList} from '../../utils/typing'
import {metaAsDict} from '../../utils/data'
import {formatCurrency} from '../../utils/numbers'
import {Loader, Page, Container} from '../layout'
import {Order, OrderVariables, Order_orderByToken_lines} from './_gql-types/Order'
import {orderQuery} from './order-queries'
import RatioImage from '../common/RatioImage'


interface Props extends RouteComponentProps {
  token?: string
}

interface OrderLineProps {
  line: Order_orderByToken_lines
}

interface DetailItemProps {
  label: React.ReactNode
  value: React.ReactNode
}

function OrderLine({line}: OrderLineProps) {
  const {variant} = line
  const product = variant?.product
  const thumbnail = product?.thumbnail
  const thumbnail2x = product?.thumbnail2x

  const totalPrice = formatCurrency(line.totalPrice?.net.amount)

  return (
    <Link
      to={`/verslun/vorur/${product?.slug}/`}
      className="card-hover-wrapper text-reset"
    >
      <div className="card border-0 mb-3 rounded bg-white px-3 py-4 py-md-3">
        <div className="row">
          <div className="col-3 col-lg-1">
            <RatioImage
              src={thumbnail?.url}
              src2x={thumbnail2x?.url}
              ratio={[4, 3]}
              style={{minHeight: '100%'}}
            />
          </div>

          <div className="col pl-2 d-flex flex-column flex-md-row align-items-md-center">
            <h5 className="mb-0">{product?.name}</h5>
            <div className="col d-flex align-items-center justify-content-end pt-4 pt-md-0">
              <p className="mb-0 pr-4"><span className="text-muted">Magn:</span> <strong>{line.quantity}</strong></p>
              <p className="mb-0"><span className="text-muted">Verð:</span> <strong>{totalPrice} kr.</strong></p>
            </div>
          </div>
        </div>
      </div>
    </Link>
  )
}

function DetailItem({label, value}: DetailItemProps) {
  return (
    <div className="w-100 d-flex py-1 justify-content-between align-items-start">
      <p className="mb-0 pr-2">{label}</p>
      <p className="mb-0 text-right text-muted"><small><strong>{value}</strong></small></p>
    </div>
  )
}

export default function OrderDetailsPage({token}: Props) {
  token = token as string
  const {data, loading} = useQuery<Order, OrderVariables>(
    orderQuery,
    {variables: {token}},
  )

  if (loading) {
    return (
      <Page>
        <Loader />
      </Page>
    )
  }


  const order = data?.orderByToken
  if (!order) {
    return (
      <Page>
        <Container size="sm">
          <div className="py-5 d-flex flex-column align-items-center justify-content-center">
            <BagXFill size={40} className="text-muted mb-3" />

            <h5 className="text-muted">Pöntun fannst ekki</h5>
          </div>
        </Container>
      </Page>
    )
  }

  const date = formatDate(parseDateISO(order.created), 'd. MMM yyyy')

  const meta = metaAsDict(order.metadata)

  const sendDate = meta.date
    ? formatDate(parseDateISO(meta.date), 'd. MMM yyyy')
    : undefined

  const shippingPrice = order.shippingPrice?.net.amount || 0
  const netSubtotal = order?.subtotal?.net.amount
  const grossTotal = order?.total?.gross.amount || 0
  const netTotal = order?.total?.net.amount || 0
  const totalVAT = grossTotal - netTotal

  // const name = `${shippingAddress?.firstName} ${shippingAddress?.lastName}`
  const {id: orderId} = fromGlobalId(order.id)

  const {shippingAddress: sAddr} = order
  const shippingAddressStr = sAddr
    ? `${sAddr.streetAddress1}, ${sAddr.postalCode} ${sAddr.city}`
    : ''

  return (
    <Page>
      <Container size="lg">
        <div className="py-5">
          <div className="d-flex align-items-center justify-content-between mb-5">
            <div className="d-flex align-items-center">
              <BagCheckFill size={36} className="mb-2 mr-3 text-primary" />
              <h1 className="mb-0">Pöntunin þín <span className="text-muted">#{orderId}</span></h1>
            </div>

            <h4 className="text-muted mb-0">
              <CalendarEvent size={20} className="mr-3 mb-1" />
              {date}
            </h4>
          </div>

          <div className="row">
            <div className="col-12 col-md-8">
              {ensureList(order.lines).map((line) => (
                <OrderLine key={line.id} line={line} />
              ))}
            </div>

            <div className="col-12 col-md-4">
              <div className="p-4 bg-white rounded border border-gray">
                {/* <DetailItem label="Nafn á pöntun" value={name} /> */}

                {/* <DetailItem label="Staða sendingar" value={order.status} /> */}

                <DetailItem label="Sendingarmáti" value={order.shippingMethod?.name || ''} />

                <DetailItem label="Sendist til" value={shippingAddressStr} />

                <DetailItem label="Dagur sendingar" value={sendDate} />

                <div className="pb-1 bg-light my-3" />

                <DetailItem label="Verð" value={`${formatCurrency(netSubtotal)} kr.`} />

                <DetailItem label="Sendingarkostnaður" value={`+ ${formatCurrency(shippingPrice)} kr.`} />

                <DetailItem label="VSK" value={`+ ${formatCurrency(totalVAT)} kr.`} />

                <div className="w-100 d-flex py-1 justify-content-between align-items-start">
                  <p className="mb-0 pr-2"><strong>Samtals</strong></p>
                  <p className="mb-0 text-right"><strong>{formatCurrency(grossTotal)} kr.</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Page>
  )
}
